import { skipToken } from "@reduxjs/toolkit/query"
import {
  CashRegisterTransactionType,
  PaymentsByDayTransactionType,
  SalesTransactionType,
} from "gen/analyticsv2/models_pb"
import { ListAccountsReq } from "gen/api/accounting/service_pb"
import { Account } from "gen/coa/models_pb"
import {
  TaxRate,
  TaxRate_Type as TaxRateType,
  Location,
  ProductCategory,
  Staff,
  FinancingTerms_Term as FinancingTerms,
  Entity,
  AdditionalFees_Fee,
} from "gen/company/models_pb"
import { Method, Method_Type as MethodType } from "gen/company/tender/models_pb"
import { Customer } from "gen/customer/models_pb"
import { Product } from "gen/product/models_pb"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import { Tag, Tag_Type } from "gen/tag/models_pb"
import { Vendor } from "gen/vendors/models_pb"
import { NextRouter, useRouter } from "next/router"
import { ReactElement, useMemo } from "react"
import { UUID } from "shared-ts/classes/uuid"
import { stringComparator } from "shared-ts/utils/sorting/comparators"

import { Filter } from "components/composite/Filter"
import { FilterChip } from "components/composite/FilterChip"
import { useListAccountsQuery } from "features/api/accounting"
import {
  useListAdditionalFeesForCompanyQuery,
  useListEntitiesForCompanyQuery,
  useListFinancingTermsForCompanyQuery,
  useListLocationsForCompanyQuery,
  useListProductCategoriesForCompanyV2Query,
  useListStaffsForCompanyQuery,
  useListTaxRatesForCompanyQuery,
} from "features/api/company"
import {
  useSearchCustomersV2,
  useSearchJobs,
  useSearchProductsV2,
  useSearchTags,
  useSearchTenderMethods,
} from "features/api/search"
import { useListVendorsForCompanyQuery } from "features/api/vendor"

import { extractParams, queryParamValue } from "./configuration/helpers"
import {
  ACCOUNT_PARAM,
  CLASS_PARAM,
  CLERK_PARAM,
  CUSTOMER_PARAM,
  CUSTOMER_TAG_PARAM,
  DEPARTMENT_PARAM,
  ENTITY_PARAM,
  FEE_PARAM,
  FINELINE_PARAM,
  JOB_PARAM,
  LOCATION_PARAM,
  METHOD_PARAM,
  PRODUCT_PARAM,
  PRODUCT_TAG_PARAM,
  REP_PARAM,
  SECONDARY_LOCATION_PARAM,
  TAX_RATE_PARAM,
  TERMS_PARAM,
  VENDOR_PARAM,
  extractItemsFromParam,
  joinParameters,
  parseParamKeys,
  pushReportParam,
} from "./params"
import { Filters, FilterT } from "./types"

export type FilterTypeUnion =
  | Staff
  | Location
  | Customer
  | Product
  | Vendor
  | ProductCategory
  | Account
  | PaymentsByDayTransactionType
  | CashRegisterTransactionType
  | SalesTransactionType
  | FinancingTerms
  | Entity
  | Method
  | AdditionalFees_Fee

export type Enumeration = Record<string, string | number>
export type EnumValue = number

export type ReportingFilterUnion =
  | ReportingFilter<Staff>
  | ReportingFilter<Location>
  | ReportingFilter<Customer>
  | ReportingFilter<Product>
  | ReportingFilter<Tag>
  | ReportingFilter<Vendor>
  | ReportingFilter<ProductCategory>
  | ReportingFilter<Account>
  | ReportingFilter<FinancingTerms>
  | ReportingFilter<Entity>
  | ReportingFilter<Method>
  | ReportingFilter<AdditionalFees_Fee>
  | ReportingFilter<EnumValue>

interface ReportingDynamicFilterProps {
  filters: Filters
  customFilters: (router: NextRouter) => (ReportingFilterUnion | null)[]
}

function toFilterProps(filter: FilterT | undefined) {
  const label = typeof filter === "string" ? filter : filter?.label ?? ""
  return {
    label,
    skip: !label,
  }
}

export function useReportingDynamicFilter({
  filters,
  customFilters,
}: ReportingDynamicFilterProps): ReportingFilterUnion[] {
  const router = useRouter()
  return [
    useLocationFilter({ ...toFilterProps(filters.location), router }),
    useSecondaryLocationFilter({
      ...toFilterProps(filters.secondaryLocation),
      router,
    }),
    useClerkFilter({ ...toFilterProps(filters.clerk), router }),
    useRepFilter({ ...toFilterProps(filters.rep), router }),
    useCustomerFilter({ ...toFilterProps(filters.customer), router }),
    useCustomerTagsFilter({ ...toFilterProps(filters.customerTag), router }),
    useProductFilter({ ...toFilterProps(filters.product), router }),
    useProductTagsFilter({ ...toFilterProps(filters.productTag), router }),
    useVendorFilter({ ...toFilterProps(filters.vendor), router }),
    useDepartmentFilter({ ...toFilterProps(filters.department), router }),
    useClassFilter({ ...toFilterProps(filters.class), router }),
    useFinelineFilter({ ...toFilterProps(filters.fineline), router }),
    useAccountFilter({ ...toFilterProps(filters.account), router }),
    useTermsFilter({ ...toFilterProps(filters.terms), router }),
    useJobFilter({ ...toFilterProps(filters.job), router }),
    useTaxRateFilter({ ...toFilterProps(filters.taxRate), router }),
    useEntityFilter({ ...toFilterProps(filters.entity), router }),
    useMethodFilter({
      ...toFilterProps(filters.method),
      router,
      excludeWriteOff: filters.method?.excludeWriteOff ?? false,
      excludeChargeAccount: filters.method?.excludeChargeAccount ?? false,
    }),
    useFeeFilter({ ...toFilterProps(filters.fee), router }),
    ...customFilters(router),
  ]
    .filter((f): f is ReportingFilterUnion => !!f)
    .sort((a, b) => a.stagedFilter.title.localeCompare(b.stagedFilter.title))
}

export interface ReportingFilter<T> {
  stagedFilter: Filter<T>
  stagedChip: ReactElement | null
  appliedChip: ReactElement | null
}

interface ReportingFilterProps {
  label: string
  skip: boolean
  router: NextRouter
}

export function useLocationFilter(
  props: ReportingFilterProps
): ReportingFilter<Location> | null {
  const { currentData: listLocationsData } = useListLocationsForCompanyQuery(
    !props.skip
      ? {
          filterActive: false,
        }
      : skipToken
  )

  return buildIDFilter<Location>({
    ...props,
    allItems: listLocationsData?.locations ?? [],
    nameExtractor: (l) => l.name,
    idExtractor: (l) => l.id,
    paramKey: LOCATION_PARAM,
  })
}

export function useSecondaryLocationFilter(
  props: ReportingFilterProps
): ReportingFilter<Location> | null {
  const { currentData: listLocationsData } = useListLocationsForCompanyQuery(
    !props.skip
      ? {
          filterActive: false,
        }
      : skipToken
  )

  return buildIDFilter<Location>({
    ...props,
    allItems: listLocationsData?.locations ?? [],
    nameExtractor: (l) => l.name,
    idExtractor: (l) => l.id,
    paramKey: SECONDARY_LOCATION_PARAM,
  })
}

export function useTaxRateFilter(
  props: ReportingFilterProps
): ReportingFilter<TaxRate> | null {
  const { data } = useListTaxRatesForCompanyQuery(
    !props.skip
      ? {
          filterActive: true,
          filterTypes: [TaxRateType.LOCATION_RATE],
        }
      : skipToken
  )

  return buildIDFilter<TaxRate>({
    ...props,
    allItems: data?.taxRates ?? [],
    nameExtractor: (t) => t.name,
    idExtractor: (t) => t.id,
    paramKey: TAX_RATE_PARAM,
    label: "Tax Rate",
  })
}

export function useEntityFilter(
  props: ReportingFilterProps
): ReportingFilter<Entity> | null {
  const { data } = useListEntitiesForCompanyQuery(!props.skip ? {} : skipToken)
  return buildIDFilter<Entity>({
    ...props,
    allItems: data?.entities ?? [],
    nameExtractor: (e) => e.name,
    idExtractor: (e) => e.id,
    paramKey: ENTITY_PARAM,
  })
}

export function useVendorFilter(
  props: ReportingFilterProps
): ReportingFilter<Vendor> | null {
  const { currentData: listVendorsData } = useListVendorsForCompanyQuery(
    !props.skip
      ? {
          filterActive: false,
          filterIds: [],
          filterConnectedIntegrationVendor: false,
        }
      : skipToken
  )

  return buildIDFilter<Vendor>({
    ...props,
    allItems: listVendorsData?.vendors ?? [],
    nameExtractor: (l) => l.name,
    idExtractor: (l) => l.id,
    paramKey: VENDOR_PARAM,
  })
}

export function useDepartmentFilter(
  props: ReportingFilterProps
): ReportingFilter<ProductCategory> | null {
  const { currentData: listCategories } =
    useListProductCategoriesForCompanyV2Query(!props.skip ? {} : skipToken)

  return buildIDFilter<ProductCategory>({
    skip: props.skip,
    label: props.label,
    router: props.router,
    allItems: listCategories?.departments ?? [],
    nameExtractor: (l) => l.name,
    idExtractor: (l) => l.id,
    paramKey: DEPARTMENT_PARAM,
  })
}

export function useClassFilter(
  props: ReportingFilterProps
): ReportingFilter<ProductCategory> | null {
  const { currentData: listCategories } =
    useListProductCategoriesForCompanyV2Query(!props.skip ? {} : skipToken)

  return buildIDFilter<ProductCategory>({
    ...props,
    allItems: listCategories?.classes ?? [],
    nameExtractor: (l) => l.name,
    idExtractor: (l) => l.id,
    paramKey: CLASS_PARAM,
  })
}

export function useAccountFilter(
  props: ReportingFilterProps
): ReportingFilter<Account> | null {
  const { currentData } = useListAccountsQuery(
    !props.skip ? ListAccountsReq.create() : skipToken
  )

  return buildIDFilter<Account>({
    ...props,
    allItems: currentData?.accounts ?? [],
    nameExtractor: (a) => a.name,
    idExtractor: (a) => a.id,
    paramKey: ACCOUNT_PARAM,
  })
}

export function useFinelineFilter(
  props: ReportingFilterProps
): ReportingFilter<ProductCategory> | null {
  const { currentData: listCategories } =
    useListProductCategoriesForCompanyV2Query(!props.skip ? {} : skipToken)

  return buildIDFilter<ProductCategory>({
    ...props,
    allItems: listCategories?.finelines ?? [],
    nameExtractor: (l) => l.name,
    idExtractor: (l) => l.id,
    paramKey: FINELINE_PARAM,
  })
}

export function useClerkFilter(
  props: ReportingFilterProps
): ReportingFilter<Staff> | null {
  const { currentData: listStaffMembersData } = useListStaffsForCompanyQuery(
    !props.skip ? {} : skipToken
  )

  return buildIDFilter<Staff>({
    ...props,
    allItems: listStaffMembersData?.staffs ?? [],
    nameExtractor: (s) => `${s.firstName} ${s.lastName}`,
    idExtractor: (s) => s.id,
    paramKey: CLERK_PARAM,
  })
}

export function useRepFilter(
  props: ReportingFilterProps
): ReportingFilter<Staff> | null {
  const { currentData: listStaffMembersData } = useListStaffsForCompanyQuery(
    !props.skip ? {} : skipToken
  )

  return buildIDFilter<Staff>({
    ...props,
    allItems: listStaffMembersData?.staffs ?? [],
    nameExtractor: (s) => `${s.firstName} ${s.lastName}`,
    idExtractor: (s) => s.id,
    paramKey: REP_PARAM,
  })
}

export function useCustomerFilter(
  props: ReportingFilterProps
): ReportingFilter<Customer> | null {
  const {
    query: customerQuery,
    setQuery: setCustomerQuery,
    results: customerResults,
  } = useSearchCustomersV2({}, { skip: props.skip })

  const allCustomers = useMemo(() => {
    return customerResults
      .map((r) => r.customer)
      .filter((c): c is Customer => !!c)
  }, [customerResults])

  return buildIDFilter<Customer>({
    ...props,
    allItems: allCustomers,
    nameExtractor: (c) => c.name,
    idExtractor: (c) => c.id,
    paramKey: CUSTOMER_PARAM,
    query: customerQuery,
    onChangeQuery: setCustomerQuery,
  })
}

export function useJobFilter(
  props: ReportingFilterProps
): ReportingFilter<Customer> | null {
  const {
    query: jobQuery,
    setQuery: setJobQuery,
    results: jobResults,
  } = useSearchJobs({}, { skip: props.skip })

  const allJobs = useMemo(() => {
    return jobResults.map((r) => r.job).filter((c): c is Customer => !!c)
  }, [jobResults])

  return buildIDFilter<Customer>({
    ...props,
    allItems: allJobs,
    nameExtractor: (c) => c.name,
    idExtractor: (c) => c.id,
    paramKey: JOB_PARAM,
    query: jobQuery,
    onChangeQuery: setJobQuery,
  })
}

export function useCustomerTagsFilter(props: ReportingFilterProps) {
  const {
    query: tagsQuery,
    setQuery: setTagsQuery,
    results: tagsResults,
  } = useSearchTags({
    types: [Tag_Type.CUSTOMER],
  })

  const allTags = useMemo(() => {
    return tagsResults.map((r) => r.tag).filter((t): t is Tag => !!t)
  }, [tagsResults])

  return buildFilter<Tag>({
    ...props,
    allItems: allTags,
    nameExtractor: (t) => t.name,
    keyExtractor: (t) => UUID.idString(t.id),
    paramKey: CUSTOMER_TAG_PARAM,
    query: tagsQuery,
    onChangeQuery: setTagsQuery,
  })
}

export function useProductFilter(
  props: ReportingFilterProps
): ReportingFilter<Product> | null {
  const { query, setQuery, results } = useSearchProductsV2(
    {},
    { skip: props.skip }
  )

  const allProducts = useMemo(() => {
    return results.map((r) => r.product).filter((p): p is Product => !!p)
  }, [results])

  return buildFilter<Product>({
    ...props,
    allItems: allProducts,
    nameExtractor: (p) => p.name,
    keyExtractor: (p) => UUID.idString(p.id),
    paramKey: PRODUCT_PARAM,
    query,
    onChangeQuery: setQuery,
  })
}

export function useProductTagsFilter(props: ReportingFilterProps) {
  const {
    query: tagsQuery,
    setQuery: setTagsQuery,
    results: tagsResults,
  } = useSearchTags({
    types: [Tag_Type.PRODUCT],
  })

  const allTags = useMemo(() => {
    return tagsResults.map((r) => r.tag).filter((t): t is Tag => !!t)
  }, [tagsResults])

  return buildFilter<Tag>({
    ...props,
    allItems: allTags,
    nameExtractor: (t) => t.name,
    keyExtractor: (t) => UUID.idString(t.id),
    paramKey: PRODUCT_TAG_PARAM,
    query: tagsQuery,
    onChangeQuery: setTagsQuery,
  })
}

export function useTermsFilter(
  props: ReportingFilterProps
): ReportingFilter<FinancingTerms> | null {
  const { currentData: listTermsData } = useListFinancingTermsForCompanyQuery(
    !props.skip ? {} : skipToken
  )

  return buildIDFilter<FinancingTerms>({
    ...props,
    allItems: listTermsData?.terms ?? [],
    nameExtractor: (t) =>
      listTermsData?.namesByIdString?.[t.idString] ?? t.idString,
    idExtractor: (t) => UUID.PBUUIDFromString(t.idString),
    paramKey: TERMS_PARAM,
  })
}

interface UseMethodFilterProps extends ReportingFilterProps {
  excludeWriteOff: boolean
  excludeChargeAccount: boolean
}
export function useMethodFilter(props: UseMethodFilterProps) {
  const excludeTypes = [
    ...(props.excludeWriteOff ? [MethodType.WRITE_OFF] : []),
    ...(props.excludeChargeAccount ? [MethodType.CHARGE_ACCOUNT] : []),
  ]
  const {
    query: methodQuery,
    setQuery: setMethodQuery,
    results: methodResults,
  } = useSearchTenderMethods({ excludeTypes }, { skip: props.skip })

  const allMethods = useMemo(() => {
    return methodResults.map((r) => r.method).filter((c): c is Method => !!c)
  }, [methodResults])

  return buildIDFilter<Method>({
    ...props,
    allItems: allMethods,
    nameExtractor: (m) => m.name,
    idExtractor: (m) => m.id,
    paramKey: METHOD_PARAM,
    query: methodQuery,
    onChangeQuery: setMethodQuery,
  })
}

export function useFeeFilter(
  props: ReportingFilterProps
): ReportingFilter<AdditionalFees_Fee> | null {
  const { currentData: listFeesData } = useListAdditionalFeesForCompanyQuery(
    !props.skip ? {} : skipToken
  )

  return buildFilter<AdditionalFees_Fee>({
    ...props,
    allItems: listFeesData?.fees ?? [],
    nameExtractor: (f) => f.name,
    keyExtractor: (f) => f.idString,
    paramKey: FEE_PARAM,
  })
}

interface buildFilterParams<T> extends ReportingFilterProps {
  allItems: T[]
  nameExtractor: (t: T) => string
  keyExtractor: (t: T) => string
  paramKey: string
  query?: string
  onChangeQuery?: (s: string) => void
}

type IDFilterParams<T> = Omit<buildFilterParams<T>, "keyExtractor"> & {
  idExtractor: (t: T) => PBUUID | undefined
}

function buildIDFilter<T>(
  params: IDFilterParams<T>
): ReportingFilter<T> | null {
  return buildFilter<T>({
    ...params,
    keyExtractor: (t) => UUID.idString(params.idExtractor(t)),
  })
}

export function enumKey(m: Enumeration[keyof Enumeration]): string {
  return m.toString()
}

type EnumFilterParams<T extends Enumeration> = Omit<
  buildFilterParams<T[keyof T]>,
  "keyExtractor" | "skip" | "router" | "nameExtractor" | "allItems"
> & {
  names: Record<T[keyof T], string>
  enum: T
}
export function buildEnumFilter<T extends Enumeration>(
  params: EnumFilterParams<T>
): (router: NextRouter) => ReportingFilter<T[keyof T]> | null {
  return (router: NextRouter) => {
    const allItems = listEnumValues(params.enum).filter(Boolean)
    return buildFilter<T[keyof T]>({
      ...params,
      skip: false,
      router,
      allItems,
      keyExtractor: enumKey,
      nameExtractor: (m) => params.names[m],
    })
  }
}

export function listEnumValues<T extends Enumeration>(
  enumObj: T
): T[keyof T][] {
  return Object.values(enumObj).filter(
    (a): a is T[keyof T] => typeof a !== "string"
  )
}

export function parseEnumValues<T extends Enumeration>(
  v: queryParamValue,
  enumObj: T
): T[keyof T][] {
  const enumValues = listEnumValues(enumObj)
  return enumValues.filter((n) => extractParams(v).includes(n.toString()))
}

export function parseEnumValue<T extends Enumeration>(
  v: queryParamValue,
  enumObj: T,
  defaultValue: T[keyof T]
): T[keyof T] {
  const enumValues = listEnumValues(enumObj)
  const params = extractParams(v)
  if (params.length !== 1) {
    return defaultValue
  }
  return enumValues.find((n) => n.toString() === params[0]) ?? defaultValue
}

export function buildFilter<T>({
  router,
  label,
  skip,
  keyExtractor,
  nameExtractor,
  allItems,
  paramKey,
  query,
  onChangeQuery,
}: buildFilterParams<T>): ReportingFilter<T> | null {
  if (skip) {
    return null
  }

  const { stagedKeys, appliedKeys } = parseParamKeys(router.query)

  const stagedItems = extractItemsFromParam(
    stagedKeys[paramKey],
    allItems,
    keyExtractor
  )

  const appliedItems = extractItemsFromParam(
    appliedKeys[paramKey],
    allItems,
    keyExtractor
  )

  function handleChangeStagedItems(items: T[]) {
    pushReportParam(router, paramKey, joinParameters(items.map(keyExtractor)))
  }

  return {
    stagedFilter: {
      title: label,
      items: allItems,
      selectedItems: stagedItems,
      sortItems: stringComparator(nameExtractor),
      onChangeSelectedItems: handleChangeStagedItems,
      renderItem: (item) => <div>{nameExtractor(item)}</div>,
      nameExtractor: nameExtractor,
      keyExtractor: keyExtractor,
      query,
      onChangeQuery,
    },
    stagedChip: buildChips(
      stagedItems,
      nameExtractor,
      label,
      handleChangeStagedItems,
      false
    ),
    appliedChip: buildChips(
      appliedItems,
      nameExtractor,
      label,
      handleChangeStagedItems,
      true
    ),
  }
}

function buildChips<T>(
  items: T[],
  nameExtractor: (t: T) => string,
  label: string,
  handleChangeSelectedItems: (items: T[]) => void,
  isApplied: boolean
) {
  return items.length > 0 ? (
    <FilterChip
      selectedFilters={items.map((item) => ({
        name: nameExtractor(item),
      }))}
      filterTitle={label}
      onDelete={() => handleChangeSelectedItems([])}
      color={isApplied ? "sky" : "gray"}
    />
  ) : null
}
